import React, { FC, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import FlyoutTooltip from "./FlyoutTooltip";
import brandgenSymbol from "../../../../assets/brandgen-symbol.svg";
import {
  ArrowWrap,
  BottomMenuItem,
  FlyoutBody,
  FlyoutHeader,
  FlyoutItemEl,
  FlyoutPanel,
  LogoIcon,
  LogoName,
  LogoSymbol,
  LogoZone,
  MenuItem,
  SidebarBottom,
  SidebarNav,
  SidebarRoot,
} from "./AppSidebar.styles";
import { NAV_ITEMS, NavItem } from "./AppSidebar.config";
import { ROUTE_PATHS } from "../../../../types/RoutePaths";
import { Svg } from "./AppSidebar.icons";
import { useGlobalSelectedAdvertiser } from "../../../../hooks/useGlobalSelectedAdvertiser";
import { useIsAdminUser } from "../../../../hooks/useIsAdminUser";

const getAdvertiserDashboardPath = (brandgenAdvertiserId: string | null): string =>
  brandgenAdvertiserId ? `/advertisers/${brandgenAdvertiserId}/dashboard` : ROUTE_PATHS.ADVERTISERS_LIST;

const AppSidebar: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { brandgenAdvertiserId } = useGlobalSelectedAdvertiser();
  const isAdmin = useIsAdminUser();
  const navItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);
  const topItems = navItems.filter((i) => i.placement !== "bottom");
  const bottomItems = navItems.filter((i) => i.placement === "bottom");
  const flyoutItems = navItems.filter((i) => i.subItems?.length);
  const [activeFlyout, setActiveFlyout] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tooltipState, setTooltipState] = useState<{ title: string; text: string; anchorRect: DOMRect } | null>(null);

  useEffect(() => {
    if (activeFlyout === null) setTooltipState(null);
  }, [activeFlyout]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openFlyout = (key: string) => {
    clearCloseTimer();
    setActiveFlyout(key);
  };

  const closeFlyout = () => {
    clearCloseTimer();
    setActiveFlyout(null);
  };

  const scheduleFlyoutClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setActiveFlyout(null);
      closeTimerRef.current = null;
    }, 150);
  };

  const navigateAndClose = (path: string) => {
    history.push(path);
    setActiveFlyout(null);
    clearCloseTimer();
  };

  const isNavItemActive = (item: NavItem): boolean => {
    if (item.usesSelectedAdvertiser) {
      return /^\/advertisers\/[^/]+\/.+/.test(location.pathname);
    }

    if (item.path) {
      return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
    }

    return (item.sectionPaths ?? []).some((p) => location.pathname.startsWith(p));
  };

  const handleNavItemClick = (item: NavItem) => {
    if (item.usesSelectedAdvertiser) {
      navigateAndClose(getAdvertiserDashboardPath(brandgenAdvertiserId));
      return;
    }

    if (item.path) {
      navigateAndClose(item.path);
    }
  };

  const isSubItemActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <>
      <SidebarRoot onMouseLeave={scheduleFlyoutClose} onMouseEnter={clearCloseTimer}>
        <LogoZone>
          <LogoIcon>
            <LogoSymbol src={brandgenSymbol} alt="BrandGen" />
          </LogoIcon>
          <LogoName>BrandGen</LogoName>
        </LogoZone>

        <SidebarNav>
          {topItems.map((item) => (
            <MenuItem
              key={item.key}
              data-active={isNavItemActive(item) ? "true" : "false"}
              onMouseEnter={() => (item.subItems?.length ? openFlyout(item.key) : closeFlyout())}
              onClick={() => handleNavItemClick(item)}
            >
              {item.icon}
              {item.label}
              {item.hasArrow && (
                <ArrowWrap>
                  <Svg width="12" height="12" strokeWidth={2.5}>
                    <polyline points="9 18 15 12 9 6" />
                  </Svg>
                </ArrowWrap>
              )}
            </MenuItem>
          ))}
        </SidebarNav>

        <SidebarBottom>
          {bottomItems.map((item) => (
            <BottomMenuItem key={item.key}>
              {item.icon}
              {item.label}
            </BottomMenuItem>
          ))}
        </SidebarBottom>
      </SidebarRoot>

      {flyoutItems.map((item) => (
        <FlyoutPanel
          key={item.key}
          data-open={activeFlyout === item.key ? "true" : "false"}
          onMouseEnter={() => openFlyout(item.key)}
          onMouseLeave={scheduleFlyoutClose}
        >
          <FlyoutHeader>{item.label}</FlyoutHeader>
          <FlyoutBody>
            {item.subItems!.map((sub) => (
              <FlyoutItemEl
                key={sub.path}
                data-active={isSubItemActive(sub.path) ? "true" : "false"}
                onClick={() => navigateAndClose(sub.path)}
                onMouseEnter={
                  sub.tooltip
                    ? (e) =>
                        setTooltipState({
                          ...sub.tooltip!,
                          anchorRect: e.currentTarget.getBoundingClientRect(),
                        })
                    : undefined
                }
                onMouseLeave={sub.tooltip ? () => setTooltipState(null) : undefined}
              >
                {sub.icon}
                {sub.label}
              </FlyoutItemEl>
            ))}
          </FlyoutBody>
        </FlyoutPanel>
      ))}

      <FlyoutTooltip
        title={tooltipState?.title ?? ""}
        text={tooltipState?.text ?? ""}
        anchorRect={tooltipState?.anchorRect ?? null}
        visible={tooltipState !== null}
      />
    </>
  );
};

export default AppSidebar;
